/**
 * Router mínimo por hash — sin react-router.
 *
 * Por qué a mano: el portal tiene cinco rutas y una sola pieza con estado (la
 * pestaña del partido). Traer un router serían 20 kB y una dependencia más
 * para resolver un `split('/')`. Además el hash hace que el portal se pueda
 * servir como archivos estáticos desde cualquier lado, sin reescrituras en el
 * server.
 *
 * Gramática:
 *   #/matches
 *   #/match/:matchId            (pestaña por defecto: resumen)
 *   #/match/:matchId/:tab       tab ∈ MATCH_TABS
 *   #/competitions
 *   #/competition/:id           (pestaña por defecto: fixture)
 *   #/competition/:id/:tab      tab ∈ COMPETITION_TABS
 *   #/mesa/:matchId           (pestaña por defecto: oficiales)
 *   #/mesa/:matchId/:tab      tab ∈ MESA_TABS
 *   #/season
 *   #/planner
 *   #/teams
 *   #/import
 *   #/settings
 * Cualquier ruta admite query (`?sets=1,2`, `?q=texto`), que se parsea aparte.
 *
 * Todo acá es puro: entra un string, sale un objeto. Los tests no necesitan DOM.
 */

export const MATCH_TABS = [
  'resumen',
  'rotaciones',
  'armador',
  'zonas',
  'clips',
  'evolucion',
  'vivo',
] as const;
export type MatchTab = (typeof MATCH_TABS)[number];

export const DEFAULT_MATCH_TAB: MatchTab = 'resumen';

/**
 * Pestañas de una competencia. `fixture` primero porque es lo que la
 * federación abre un sábado: qué se juega hoy.
 */
export const COMPETITION_TABS = ['fixture', 'posiciones', 'ficha'] as const;
export type CompetitionTab = (typeof COMPETITION_TABS)[number];

export const DEFAULT_COMPETITION_TAB: CompetitionTab = 'fixture';

/**
 * Pestañas de la MESA DE CONTROL (`#/mesa/:matchId`): la pantalla del anotador
 * en la laptop, que no es la consola del banco (`#/bench`). `oficiales` va
 * primero porque es lo primero que pasa en la cancha: acreditar al equipo
 * arbitral y emitir las credenciales QR+PIN antes del primer saque.
 */
export const MESA_TABS = ['oficiales', 'challenge', 'acta'] as const;
export type MesaTab = (typeof MESA_TABS)[number];

export const DEFAULT_MESA_TAB: MesaTab = 'oficiales';

/**
 * Vistas de la consola de partido (Fase D, §5.4). Van en el hash y no en un
 * query para que el QR que la mesa muestra sea una URL completa y compartible:
 * `#/bench/<matchId>` alcanza, y el rol de la credencial elige la vista.
 */
export const BENCH_VIEW_SEGMENTS = ['banco', 'mesa', 'arbitro', 'espejo', 'tablero'] as const;
export type BenchViewSegment = (typeof BENCH_VIEW_SEGMENTS)[number];

export function isBenchViewSegment(value: string): value is BenchViewSegment {
  return (BENCH_VIEW_SEGMENTS as readonly string[]).includes(value);
}

export type RouteName =
  | 'matches'
  | 'match'
  | 'competitions'
  | 'competition'
  | 'season'
  | 'planner'
  | 'teams'
  | 'import'
  | 'settings'
  | 'bench'
  | 'mesa'
  | 'unknown';

export interface Route {
  name: RouteName;
  /** Sólo en `match`, `bench` y `mesa`. */
  matchId?: string;
  /** Sólo en `match`; siempre resuelto a una pestaña válida. */
  tab?: MatchTab;
  /** Sólo en `mesa`; siempre resuelto a una pestaña válida. */
  mesaTab?: MesaTab;
  /**
   * Sólo en `bench`, y sólo si la URL la pide explícitamente. `undefined` = la
   * elige el rol de la credencial (que es el caso normal: el árbitro escanea y
   * le sale la vista de árbitro, sin elegir nada).
   */
  benchView?: BenchViewSegment;
  /** Sólo en `competition`. */
  competitionId?: string;
  /** Sólo en `competition`; siempre resuelto a una pestaña válida. */
  competitionTab?: CompetitionTab;
  /** Query string parseada (valores crudos). */
  query: Record<string, string>;
  /** Hash normalizado que produjo esta ruta (útil para marcar el nav activo). */
  path: string;
}

export function isMatchTab(value: string): value is MatchTab {
  return (MATCH_TABS as readonly string[]).includes(value);
}

export function isCompetitionTab(value: string): value is CompetitionTab {
  return (COMPETITION_TABS as readonly string[]).includes(value);
}

export function isMesaTab(value: string): value is MesaTab {
  return (MESA_TABS as readonly string[]).includes(value);
}

/** `'?a=1&b=2'` → `{ a: '1', b: '2' }`. Tolera basura sin lanzar. */
export function parseQuery(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const clean = raw.startsWith('?') ? raw.slice(1) : raw;
  if (clean === '') return out;
  for (const part of clean.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    const key = eq === -1 ? part : part.slice(0, eq);
    const value = eq === -1 ? '' : part.slice(eq + 1);
    try {
      out[decodeURIComponent(key)] = decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      out[key] = value;
    }
  }
  return out;
}

/** `'1,2'` → `[1, 2]`; vacío o inválido → `undefined` (= todos los sets). */
export function parseSetsParam(raw: string | undefined): number[] | undefined {
  if (raw === undefined || raw.trim() === '') return undefined;
  const parsed = raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isInteger(n) && n >= 1);
  return parsed.length > 0 ? parsed : undefined;
}

/**
 * Hash del browser → ruta. Acepta `#/x`, `#x`, `/x` y `''`.
 * Lo desconocido cae en `unknown` (la UI muestra "no existe", no una pantalla en blanco).
 */
export function parseHash(raw: string): Route {
  const withoutHash = raw.startsWith('#') ? raw.slice(1) : raw;
  const queryAt = withoutHash.indexOf('?');
  const pathRaw = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const query = parseQuery(queryAt === -1 ? '' : withoutHash.slice(queryAt));

  const segments = pathRaw
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  const decoded = segments.map((s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  });

  const head = decoded[0];
  const path = `#/${decoded.join('/')}`;

  // Raíz: el archivo de partidos es la portada del portal.
  if (head === undefined) return { name: 'matches', query, path: '#/matches' };

  switch (head) {
    case 'matches':
      return { name: 'matches', query, path: '#/matches' };
    case 'season':
      return { name: 'season', query, path: '#/season' };
    // Rotation Planner: la pizarra de rotaciones (sexteto → 6 rotaciones +
    // solapamientos + estadística). Sin estado en la URL: todo es local.
    case 'planner':
      return { name: 'planner', query, path: '#/planner' };
    case 'competitions':
      return { name: 'competitions', query, path: '#/competitions' };
    case 'competition': {
      const competitionId = decoded[1];
      if (competitionId === undefined) return { name: 'unknown', query, path };
      const rawTab = decoded[2];
      const competitionTab =
        rawTab !== undefined && isCompetitionTab(rawTab) ? rawTab : DEFAULT_COMPETITION_TAB;
      return {
        name: 'competition',
        competitionId,
        competitionTab,
        query,
        path: competitionHash(competitionId, competitionTab),
      };
    }
    case 'teams':
      return { name: 'teams', query, path: '#/teams' };
    case 'import':
      return { name: 'import', query, path: '#/import' };
    case 'settings':
      return { name: 'settings', query, path: '#/settings' };
    case 'match': {
      const matchId = decoded[1];
      if (matchId === undefined) return { name: 'unknown', query, path };
      const rawTab = decoded[2];
      const tab = rawTab !== undefined && isMatchTab(rawTab) ? rawTab : DEFAULT_MATCH_TAB;
      return { name: 'match', matchId, tab, query, path: matchHash(matchId, tab) };
    }
    // Mesa de control del acta: la pantalla del anotador en la laptop. Sin
    // partido no hay mesa (misma regla que `match`): cae en `unknown`.
    case 'mesa': {
      const matchId = decoded[1];
      if (matchId === undefined) return { name: 'unknown', query, path };
      const rawTab = decoded[2];
      const mesaTab = rawTab !== undefined && isMesaTab(rawTab) ? rawTab : DEFAULT_MESA_TAB;
      return { name: 'mesa', matchId, mesaTab, query, path: mesaHash(matchId, mesaTab) };
    }
    // Consola de partido (Fase D). `#/bench` sin partido es la pantalla de
    // ingreso: se pega el QR o se tipea el código.
    case 'bench': {
      const matchId = decoded[1];
      if (matchId === undefined) return { name: 'bench', query, path: '#/bench' };
      const rawView = decoded[2];
      const benchView = rawView !== undefined && isBenchViewSegment(rawView) ? rawView : undefined;
      return {
        name: 'bench',
        matchId,
        ...(benchView !== undefined ? { benchView } : {}),
        query,
        path: benchHash(matchId, benchView),
      };
    }
    default:
      return { name: 'unknown', query, path };
  }
}

/** Hash canónico de una competencia (con pestaña). */
export function competitionHash(
  competitionId: string,
  tab: CompetitionTab = DEFAULT_COMPETITION_TAB,
): string {
  const id = encodeURIComponent(competitionId);
  return tab === DEFAULT_COMPETITION_TAB ? `#/competition/${id}` : `#/competition/${id}/${tab}`;
}

/** Hash canónico de un partido (con pestaña). */
export function matchHash(matchId: string, tab: MatchTab = DEFAULT_MATCH_TAB): string {
  const id = encodeURIComponent(matchId);
  return tab === DEFAULT_MATCH_TAB ? `#/match/${id}` : `#/match/${id}/${tab}`;
}

/** Hash canónico de la mesa de control (con pestaña). */
export function mesaHash(matchId: string, tab: MesaTab = DEFAULT_MESA_TAB): string {
  const id = encodeURIComponent(matchId);
  return tab === DEFAULT_MESA_TAB ? `#/mesa/${id}` : `#/mesa/${id}/${tab}`;
}

/**
 * Hash canónico de la consola de partido.
 *
 * Sin `view` es lo que va en el QR de la mesa: el rol de la credencial decide
 * qué pantalla sale, así que el mismo link sirve para el banco, para el árbitro
 * y para el tablero — y nadie tiene que elegir nada con la tablet en la mano.
 */
export function benchHash(matchId: string, view?: BenchViewSegment): string {
  const id = encodeURIComponent(matchId);
  return view === undefined ? `#/bench/${id}` : `#/bench/${id}/${view}`;
}

/**
 * URL de acreditación que la mesa muestra como QR.
 *
 * El token viaja en el HASH y no en la query a propósito: los fragmentos no se
 * mandan al servidor, así que el secreto de la credencial no aparece en ningún
 * access log, ni en un `Referer`, ni en el historial de un proxy de estadio.
 */
export function benchCredentialUrl(
  baseUrl: string,
  input: { org: string; matchId: string; credentialId: string; token: string },
): string {
  const q = new URLSearchParams({
    org: input.org,
    c: input.credentialId,
    t: input.token,
  }).toString();
  return `${baseUrl.replace(/\/+$/, '')}/${benchHash(input.matchId)}?${q}`;
}

/** Navegación programática (única función de este módulo que toca el browser). */
export function navigate(hash: string): void {
  const g = globalThis as { location?: { hash: string } };
  if (g.location) g.location.hash = hash;
}
